import React, { ReactElement, useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  useNavigate,
} from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import "./index.css";
import { AppsProvider } from "./context/AppsContext";
import { DarkModeProvider } from "./context/ColorModeContext.tsx";
import { CurrentAppProvider } from "./context/CurrentAppContext.tsx";

import Login from "./programmes/Login.tsx";
import ProgramOverview from "./programmes/ProgramOverview.tsx";
import OnOffArticle from "./programmes/OnOffArticle.tsx";
import OnOffList from "./programmes/OnOffList.tsx";
import ItemPricing from "./programmes/ItemPricing.tsx";
import OrderProcessing from "./programmes/OrderProcessing.tsx";
import LabelPrinter from "./programmes/LabelPrinter.tsx";
import ItemCreator from "./programmes/ItemCreator.tsx";
import AdminPanel from "./programmes/AdminPanel.tsx";
import ListingCreator from "./programmes/ListingCreator.tsx";
import StatisticsAnalyzer from "./programmes/StatisticsAnalyzer.tsx";
import DataChecker from "./programmes/DataChecker.tsx";

const backendUrl = import.meta.env.VITE_REACT_APP_BACKEND_URL;

export const logoutComponent = () => {

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const navigate = useNavigate();

  const logout = () => {
    navigate("/");
    localStorage.removeItem("token");
    window.location.reload();
  };

  return <button onClick={() => logout()}>Logout</button>;
};

export const isAuthenticated = async (t: string): Promise<boolean> => {
  try {
    const res = await axios.post(
      `${backendUrl}/login/check_authentication/`,
      {
        token: t,
      }
    );
    const x = JSON.stringify(res.data.authenticated);
    return x === "true";
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const e = error.response?.data;
      console.log(e);
      alert(e);
    } else {
      console.error(error);
      alert(error);
    }
    return false;
  }
};

interface PrivateRouteProps {
  element: ReactElement;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ element }) => {
  const [auth, setAuth] = useState<boolean | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        const response = await isAuthenticated(token);
        setAuth(response);
      } else {
        setAuth(false);
      }
    };

    checkAuth();
  }, []);

  if (auth === null) {
    return <div>Loading...</div>;
  }

  return auth ? element : <Navigate to="/" />;
};

const App: React.FC = () => {
  const router = createBrowserRouter([
    {
      path: "/",
      element: <Login />,
    },
    {
      path: "/ProgramOverview",
      element: <PrivateRoute element={<ProgramOverview />} />,
    },
    {
      path: "/OnOffArticle",
      element: <PrivateRoute element={<OnOffArticle />} />,
    },
    {
      path: "/OnOffList",
      element: <PrivateRoute element={<OnOffList />} />,
    },
    {
      path: "/ItemPricing",
      element: <PrivateRoute element={<ItemPricing />} />,
    },
    {
      path: "/OrderProcessing",
      element: <PrivateRoute element={<OrderProcessing />} />,
    },
    {
      path: "/LabelPrinter",
      element: <PrivateRoute element={<LabelPrinter />} />,
    },
    {
      path: "/ItemCreator",
      element: <PrivateRoute element={<ItemCreator />} />
    },
    {
      path: "/DataChecker",
      element: <PrivateRoute element={<DataChecker/>}/>
    },
    {
      path: "/AdminPanel",
      element: <PrivateRoute element={<AdminPanel />} />
    },
    {
      path: "/ListingCreator",
      element: <PrivateRoute element={<ListingCreator />} />
    },
    // {
    //   path: "/ListingCreator/:nonce?", // This :nonce? is for reloading the page. doing navigate(0) or window.location.reload() at at the end of the ListingCreator makes opening the ItemCreator throw an error for some reason
    //   element: <PrivateRoute element={<ListingCreator />} />
    // },
    {
      path: "/StatisticsAnalyzer",
      element: <PrivateRoute element={<StatisticsAnalyzer/>}/>
    }
  ]);

  return (
    <DarkModeProvider>
      <RouterProvider router={router}/>
    </DarkModeProvider>
  );
};

ReactDOM.createRoot(document.getElementById("root")!).render(
  // <React.StrictMode>
  <>
    <CssBaseline />
    <AppsProvider>
      <CurrentAppProvider>
        <App />
      </CurrentAppProvider>
    </AppsProvider>
  </>
  // </React.StrictMode>
);
